/**
 * LoginScreen — Battle Carp themed sign-in.
 *
 * Layout: vertically centered card on the canvas background. The stacked
 * brand logo (armed carp over the WAR/CARP wordmark) at the top, then two field-manual
 * styled fields (email / password) and the blood-red "REPORT FOR
 * DUTY" primary action. Theme-aware via semantic Tailwind tokens
 * (canvas / paper / ink / olive / brass / blood) and the brand logo's
 * `variant` prop.
 *
 * When the backend returns 403 UNVERIFIED_EMAIL we surface an inline
 * notice with a "Resend confirmation email" button instead of a generic
 * toast error.
 */

import React, { useState } from 'react'
import { View, Text } from 'react-native'
import { Link, useRouter, useLocalSearchParams } from 'expo-router'
import { BrandMark } from '@warcarp/ui'
import { useAuth } from '../../hooks/useAuth'
import { useTheme } from '../../hooks/useTheme'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { KeyboardAwareScrollView } from '../../components/ui/KeyboardAwareScrollView'
import { useToast } from '../../components/ui/Toast'
import { apiFetch, HttpError } from '../../lib/api'
import { resendConfirmation } from '../../lib/api'
import { parseRedirectParam } from '../../lib/deepLinkHelpers'
import type { TokenResponse, UnverifiedEmailError } from '@warcarp/api-types'

export default function LoginScreen() {
  const { setSession } = useAuth()
  const { theme } = useTheme()
  const { showToast } = useToast()
  const router = useRouter()
  const { redirect } = useLocalSearchParams<{ redirect?: string }>()
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<{ identifier?: string; password?: string }>({})
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null)
  const [resendLoading, setResendLoading] = useState(false)

  function validate() {
    const errs: typeof errors = {}
    if (!identifier.trim()) errs.identifier = 'Email or username is required'
    if (!password) errs.password = 'Password is required'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  async function handleLogin() {
    if (!validate()) return
    setUnverifiedEmail(null)
    setLoading(true)
    try {
      // Use apiFetch directly so we can inspect 403 bodies before throwing.
      const res = await apiFetch('/api/v1/auth/login', {
        method: 'POST',
        body: JSON.stringify({ identifier: identifier.trim(), password }),
      })

      if (res.status === 403) {
        const body = await res.json().catch(() => ({})) as UnverifiedEmailError
        if (body.error === 'UNVERIFIED_EMAIL') {
          // UNVERIFIED_EMAIL responses include the user's email so we
          // can render the resend banner regardless of whether the
          // user logged in with their email or their username.
          setUnverifiedEmail(body.email ?? identifier)
          return
        }
        throw new HttpError(403, body.message ?? 'Forbidden')
      }

      if (!res.ok) {
        const err = await res.json().catch(() => ({})) as { message?: string }
        throw new HttpError(res.status, err.message ?? `HTTP ${res.status}`)
      }

      // Parse the token response and hydrate the auth context directly —
      // no second round-trip needed since we already have the successful body.
      const tokens = await res.json() as TokenResponse
      await setSession(tokens)

      // Resume the originally-requested destination if a valid ?redirect was
      // captured (fast path for a direct sign-in). Otherwise go to (app) — the
      // (app) guard consumes any persisted pending redirect from there, which
      // covers flows that dropped the param (sign-up → confirmation → login).
      const resumePath = parseRedirectParam(redirect)
      router.replace((resumePath ?? '/(app)') as Parameters<typeof router.replace>[0])
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Login failed'
      showToast(msg, 'error')
    } finally {
      setLoading(false)
    }
  }

  async function handleResend() {
    if (!unverifiedEmail) return
    setResendLoading(true)
    try {
      await resendConfirmation(unverifiedEmail)
      showToast('Confirmation email sent', 'success')
    } catch {
      showToast('Could not resend — please try again', 'error')
    } finally {
      setResendLoading(false)
    }
  }

  return (
    <KeyboardAwareScrollView
      className="flex-1 bg-canvas"
      contentContainerStyle={{ flexGrow: 1, justifyContent: 'center', paddingHorizontal: 24, paddingVertical: 48 }}
    >
        <View className="gap-6">
          {/* Stacked brand logo: the armed carp over the wordmark */}
          <View className="items-center gap-1">
            <BrandMark kind="stacked" width={210} variant={theme === 'dark' ? 'dark' : 'light'} />
            <Text className="font-typed text-xs text-olive">— Strategic Aquatic Warfare Command —</Text>
          </View>

          <View className="gap-3">
            <Input
              label="Email or username"
              value={identifier}
              onChangeText={(v) => { setIdentifier(v); setUnverifiedEmail(null) }}
              autoCapitalize="none"
              autoCorrect={false}
              autoComplete="username"
              error={errors.identifier}
              // Enter / Return submits from any field, like an HTML form on desktop.
              onSubmitEditing={() => { if (!loading) void handleLogin() }}
            />
            <Input
              label="Password"
              value={password}
              onChangeText={setPassword}
              secureTextEntry
              autoComplete="password"
              error={errors.password}
              returnKeyType="go"
              onSubmitEditing={() => { if (!loading) void handleLogin() }}
            />
            <Link href="/(auth)/forgot-password" className="font-typed text-xs text-blood self-end italic">
              // lost the cipher?
            </Link>
          </View>

          {/* Unverified email inline notice */}
          {unverifiedEmail !== null && (
            <View className="bg-paper-up border border-olive rounded-xl px-4 py-4 gap-3">
              <Text className="font-typed text-sm text-ink">
                Confirm your email — we sent a link to{' '}
                <Text className="text-blood">{unverifiedEmail}</Text>.
              </Text>
              <Button
                title="Resend confirmation email"
                variant="secondary"
                size="sm"
                onPress={handleResend}
                loading={resendLoading}
              />
            </View>
          )}

          <Button title="Sign In" onPress={handleLogin} loading={loading} size="lg" />

          <Text className="text-center font-typed text-sm text-ink-soft">
            Fresh meat?{' '}
            <Link href="/(auth)/register" className="font-stencil text-blood">
              ENLIST →
            </Link>
          </Text>
        </View>
    </KeyboardAwareScrollView>
  )
}
